import React, { useCallback } from 'react';
import styled from 'astroturf/react';

import { sanitizeCitationHtml } from 'js/citation_generator/sanitize';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Heading } from 'js/design_system/Heading';
import { useToast } from 'js/design_system/toasts/hooks';

import { numberReference } from './bibliography';
import { copyCitation } from './copy';
import { CitationStyleId } from './styles';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;
  padding-top: var(--spacing-xs-16, 16px);
  border-top: 1px solid var(--stroke-light, #dddde2);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);
`;

const List = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
`;

const Item = styled.li`
  margin: 0;
`;

type BibliographyListProps = {
  styleId: CitationStyleId;
  entries: string[];
};

export function BibliographyList({
  styleId,
  entries,
}: BibliographyListProps): React.ReactElement {
  const { infoToast, errorToast } = useToast();

  const numbered = entries.map((html, index) =>
    numberReference(styleId, index, html),
  );

  const onCopyAll = useCallback((): void => {
    const html = numbered.join('<br>\n');
    copyCitation(html)
      .then(() => {
        infoToast('Copied bibliography');
      })
      .catch(() => {
        errorToast('Could not copy');
      });
  }, [numbered, infoToast, errorToast]);

  return (
    <Section>
      <Header>
        <Heading as="h2" size="sm">
          Bibliography
        </Heading>
        {entries.length > 0 && (
          <Button
            type="button"
            size="sm"
            secondary
            icon="content_copy"
            onClick={onCopyAll}
          >
            Copy all
          </Button>
        )}
      </Header>
      {entries.length === 0 ? (
        <Body>
          Add citations to build a bibliography. It sorts to match the selected
          style.
        </Body>
      ) : (
        <List>
          {numbered.map((html, index) => (
            <Item key={`${index}-${html.slice(0, 24)}`}>
              <Body
                as="div"
                // Citation HTML is allowlisted in sanitizeCitationHtml.
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizeCitationHtml(html),
                }}
              />
            </Item>
          ))}
        </List>
      )}
    </Section>
  );
}
